import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { formatDate } from '../utils/helpers';
import { FiCalendar, FiCheckCircle, FiClock, FiUser, FiFilter, FiRefreshCw } from 'react-icons/fi';

const STATUS_COLORS = {
  completed:   { bg: '#d1fae5', txt: '#065f46', dot: '#10b981' },
  'in-progress':{ bg: '#dbeafe', txt: '#1e40af', dot: '#3b82f6' },
  review:      { bg: '#fef3c7', txt: '#92400e', dot: '#f59e0b' },
  todo:        { bg: '#f1f5f9', txt: '#475569', dot: '#94a3b8' },
  blocked:     { bg: '#fee2e2', txt: '#991b1b', dot: '#ef4444' },
};

const PRIORITY_COLORS = { urgent:'#7c3aed', high:'#ef4444', medium:'#f59e0b', low:'#22c55e' };

export default function DailyWork() {
  const { user } = useAuth();
  const [tasks, setTasks]       = useState([]);
  const [users, setUsers]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [date, setDate]         = useState(new Date().toISOString().split('T')[0]);
  const [filterUser, setFilterUser] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [summary, setSummary]   = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const [tasksRes, usersRes] = await Promise.all([
        api.get('/tasks?limit=500'),
        api.get('/users?limit=200'),
      ]);
      const allTasks = tasksRes.data.tasks || [];
      const allUsers = (usersRes.data.users || []).filter(u => ['team','manager','admin'].includes(u.role));
      setUsers(allUsers);

      // Filter tasks updated/completed on selected date
      const selectedDate = new Date(date);
      selectedDate.setHours(0,0,0,0);
      const nextDate = new Date(selectedDate);
      nextDate.setDate(nextDate.getDate() + 1);

      const dayTasks = allTasks.filter(t => {
        const updated = new Date(t.updatedAt || t.createdAt);
        return updated >= selectedDate && updated < nextDate;
      });

      setTasks(dayTasks);

      // Build summary per user
      const s = {};
      allUsers.forEach(u => {
        const userTasks = dayTasks.filter(t =>
          (t.assignees || []).some(a => (a.id || a) === u.id)
        );
        s[u.id] = {
          total: userTasks.length,
          completed: userTasks.filter(t => t.status === 'completed').length,
          inProgress: userTasks.filter(t => t.status === 'in-progress').length,
          review: userTasks.filter(t => t.status === 'review').length,
          todo: userTasks.filter(t => t.status === 'todo').length,
        };
      });
      setSummary(s);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [date]);

  const filtered = tasks.filter(t => {
    if (filterUser && !(t.assignees || []).some(a => (a.id || a) === parseInt(filterUser))) return false;
    if (filterStatus && t.status !== filterStatus) return false;
    return true;
  });

  const totalCompleted = tasks.filter(t => t.status === 'completed').length;
  const totalInProgress = tasks.filter(t => t.status === 'in-progress').length;

  const S = {
    page: { padding: '28px 32px', background: '#f8fafc', minHeight: '100vh' },
    card: { background: '#fff', borderRadius: 14, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', padding: '20px 24px', marginBottom: 20 },
    statCard: (color) => ({ background: '#fff', borderRadius: 14, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', padding: '18px 22px', borderLeft: `4px solid ${color}`, flex: 1 }),
    badge: (status) => ({ ...STATUS_COLORS[status] || STATUS_COLORS.todo, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 5 }),
    th: { padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: '2px solid #f1f5f9' },
    td: { padding: '12px 14px', fontSize: 13, color: '#374151', borderBottom: '1px solid #f8fafc', verticalAlign: 'middle' },
  };

  return (
    <div style={S.page}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: '#0f172a' }}>📋 Daily Work Report</h1>
          <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 13 }}>Track what your team worked on each day</p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <input type="date" value={date} onChange={e => setDate(e.target.value)}
            style={{ padding: '8px 14px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: 13, fontWeight: 600, color: '#0f172a' }} />
          <button onClick={load} style={{ padding: '8px 14px', borderRadius: 8, background: '#f1f5f9', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, fontSize: 13, color: '#475569' }}>
            <FiRefreshCw size={14}/> Refresh
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
        <div style={S.statCard('#10b981')}>
          <div style={{ fontSize: 28, fontWeight: 800, color: '#10b981' }}>{totalCompleted}</div>
          <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600, marginTop: 2 }}>✅ Completed Today</div>
        </div>
        <div style={S.statCard('#3b82f6')}>
          <div style={{ fontSize: 28, fontWeight: 800, color: '#3b82f6' }}>{totalInProgress}</div>
          <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600, marginTop: 2 }}>🔄 In Progress</div>
        </div>
        <div style={S.statCard('#f59e0b')}>
          <div style={{ fontSize: 28, fontWeight: 800, color: '#f59e0b' }}>{tasks.filter(t => t.status === 'review').length}</div>
          <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600, marginTop: 2 }}>👀 In Review</div>
        </div>
        <div style={S.statCard('#7c3aed')}>
          <div style={{ fontSize: 28, fontWeight: 800, color: '#7c3aed' }}>{tasks.length}</div>
          <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600, marginTop: 2 }}>📌 Total Updated</div>
        </div>
      </div>

      {/* Per-User Summary */}
      <div style={S.card}>
        <div style={{ fontSize: 14, fontWeight: 800, color: '#0f172a', marginBottom: 16 }}>👥 Team Summary — {new Date(date).toLocaleDateString('en-US', { weekday:'long', day:'numeric', month:'long', year:'numeric' })}</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px,1fr))', gap: 12 }}>
          {users.filter(u => summary[u.id]?.total > 0).map(u => (
            <div key={u.id} style={{ background: '#f8fafc', borderRadius: 10, padding: '14px 16px', border: '1.5px solid #e2e8f0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--pm-gradient)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 14 }}>
                  {u.name?.charAt(0).toUpperCase()}
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13, color: '#0f172a' }}>{u.name}</div>
                  <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'capitalize' }}>{u.role}</div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {summary[u.id]?.completed > 0 && <span style={{ background: '#d1fae5', color: '#065f46', padding: '2px 8px', borderRadius: 8, fontSize: 11, fontWeight: 700 }}>✅ {summary[u.id].completed} done</span>}
                {summary[u.id]?.inProgress > 0 && <span style={{ background: '#dbeafe', color: '#1e40af', padding: '2px 8px', borderRadius: 8, fontSize: 11, fontWeight: 700 }}>🔄 {summary[u.id].inProgress} in progress</span>}
                {summary[u.id]?.review > 0 && <span style={{ background: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: 8, fontSize: 11, fontWeight: 700 }}>👀 {summary[u.id].review} review</span>}
                {summary[u.id]?.todo > 0 && <span style={{ background: '#f1f5f9', color: '#475569', padding: '2px 8px', borderRadius: 8, fontSize: 11, fontWeight: 700 }}>📌 {summary[u.id].todo} todo</span>}
              </div>
            </div>
          ))}
          {users.filter(u => summary[u.id]?.total > 0).length === 0 && !loading && (
            <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '30px', color: '#94a3b8', fontSize: 13 }}>
              No task activity found for {formatDate(date)}
            </div>
          )}
        </div>
      </div>

      {/* Filters + Task Table */}
      <div style={S.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: '#0f172a' }}>Task Details</div>
          <div style={{ display: 'flex', gap: 10 }}>
            <select value={filterUser} onChange={e => setFilterUser(e.target.value)}
              style={{ padding: '7px 12px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: 12, fontWeight: 600, color: '#475569' }}>
              <option value="">All Members</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
              style={{ padding: '7px 12px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: 12, fontWeight: 600, color: '#475569' }}>
              <option value="">All Statuses</option>
              <option value="completed">Completed</option>
              <option value="in-progress">In Progress</option>
              <option value="review">In Review</option>
              <option value="todo">Todo</option>
              <option value="blocked">Blocked</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>Loading...</div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8', fontSize: 13 }}>No tasks found for the selected filters</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  <th style={S.th}>Task</th>
                  <th style={S.th}>Project</th>
                  <th style={S.th}>Assignee</th>
                  <th style={S.th}>Status</th>
                  <th style={S.th}>Priority</th>
                  <th style={S.th}>Last Updated</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(t => (
                  <tr key={t.id} style={{ transition: 'background .15s' }}
                    onMouseEnter={e => e.currentTarget.style.background='#f8fafc'}
                    onMouseLeave={e => e.currentTarget.style.background='#fff'}>
                    <td style={S.td}>
                      <div style={{ fontWeight: 600, color: '#0f172a', fontSize: 13 }}>{t.title}</div>
                      {t.description && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{t.description?.slice(0,60)}{t.description?.length > 60 ? '…' : ''}</div>}
                    </td>
                    <td style={S.td}><span style={{ fontSize: 12, color: '#6366f1', fontWeight: 600 }}>{t.project?.name || '—'}</span></td>
                    <td style={S.td}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {(t.assignees || []).slice(0,3).map(a => (
                          <span key={a.id||a} style={{ background: '#ede9fe', color: '#5b21b6', padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600 }}>
                            {a.name || users.find(u=>u.id===(a.id||a))?.name || '—'}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td style={S.td}>
                      <span style={{ background: STATUS_COLORS[t.status]?.bg||'#f1f5f9', color: STATUS_COLORS[t.status]?.txt||'#475569', padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>
                        {t.status}
                      </span>
                    </td>
                    <td style={S.td}>
                      <span style={{ color: PRIORITY_COLORS[t.priority]||'#94a3b8', fontWeight: 700, fontSize: 12, textTransform: 'capitalize' }}>● {t.priority||'—'}</span>
                    </td>
                    <td style={S.td} title={t.updatedAt}>
                      <span style={{ fontSize: 12, color: '#64748b' }}>{new Date(t.updatedAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
